import { DiscussionContextType, QUERY_KEYS } from "@cnx/common";
import { getQueryKeysForDiscussionContext } from "utils/chat";
import {
  Box,
  BoxProps,
  Burger,
  ElementProps,
  Flex,
  Portal,
  Transition,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { HEADER_HEIGHT } from "common/constants";
import HeaderLogo from "components/HeaderLogo";
import NotificationWidget from "components/NotificationWidget";
import UserAvatar from "components/UserAvatar";
import { useUnreadMessagesCount } from "hooks/useUnreadMessagesCount";
import { useChatPanelStore } from "stores/chatPanel";
import IconMessageDotsCircle from "icons/common/message-dots-circle.svg";
import { motion } from "motion/react";
import { useEffect } from "react";
import { Link } from "react-router";
import { useSocket } from "src/socket/useSocket";
import { useAuthStore } from "stores/authStore";
import NavigationMenu from "./NavigationMenu/NavigationMenu";

const scaleY = {
  in: { opacity: 1, transform: "scaleY(1)" },
  out: { opacity: 0, transform: "scaleY(0)" },
  common: { transformOrigin: "top" },
  transitionProperty: "transform, opacity",
};

interface HeaderResponsiveProps {
  visible: boolean;
}

const MBox = motion.create(Box<BoxProps & ElementProps<"div", keyof BoxProps>>);

const variants = {
  hidden: { top: -HEADER_HEIGHT },
  visible: { top: 0 },
};

const HeaderResponsive = ({ visible }: HeaderResponsiveProps) => {
  const currentUserId = useAuthStore(state => state.currentUserId);
  const avatar = useAuthStore(state => state.avatar);
  const fullName = useAuthStore(state => state.fullName);
  const socket = useSocket();
  const queryClient = useQueryClient();
  const { unreadMessagesCount, isChatEnabled } = useUnreadMessagesCount();
  const setIsPanelOpen = useChatPanelStore(store => store.setIsPanelOpen);
  const [opened, { toggle, close }] = useDisclosure(false);

  useEffect(() => {
    if (!socket || !currentUserId || !isChatEnabled) return;

    const invalidateQueries = ({
      context,
    }: { context?: DiscussionContextType } = {}) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DISCUSSIONS],
      });

      if (context) {
        getQueryKeysForDiscussionContext(context)?.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
    };

    const invalidateMessageQueries = ({
      discussionId,
      senderId,
    }: {
      discussionId: string;
      senderId?: string;
    }) => {
      if (senderId === currentUserId) return;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MESSAGES, discussionId],
      });
    };

    socket.on("invalidateDiscussionQueries", invalidateQueries);
    socket.on("invalidateMessageQueries", invalidateMessageQueries);

    return () => {
      socket.off("invalidateDiscussionQueries", invalidateQueries);
      socket.off("invalidateMessageQueries", invalidateMessageQueries);
    };
  }, [socket, currentUserId, isChatEnabled, queryClient]);

  return (
    <MBox
      variants={variants}
      animate={visible ? "visible" : "hidden"}
      transition={{ ease: "easeInOut", duration: 0.15 }}
      px={"sm"}
      className="bg-white shadow-sm flex items-center sticky z-10 w-full"
      component={"header"}
      h={HEADER_HEIGHT}
    >
      <Flex w={"100%"} justify={"space-between"} align={"center"}>
        <Link to="/home" onClick={close}>
          <HeaderLogo />
        </Link>
        <Flex gap={"sm"}>
          {isChatEnabled && (
            <button
              type="button"
              aria-label="Open chat"
              className="m-2 relative"
              onClick={() => setIsPanelOpen(true)}
            >
              {unreadMessagesCount > 0 ? (
                <>
                  <Flex
                    justify="center"
                    align="center"
                    fz={10}
                    w={16}
                    h={16}
                    bg={"var(--color-blue)"}
                    className="text-white rounded-full absolute"
                    style={{
                      top: 0,
                      left: 0,
                      transform: "translate(-25%, -20%)",
                      zIndex: 2,
                      pointerEvents: "none",
                    }}
                  >
                    {unreadMessagesCount}
                  </Flex>
                  <IconMessageDotsCircle
                    width={24}
                    height={24}
                    color="var(--color-blue)"
                  />
                </>
              ) : (
                <IconMessageDotsCircle width={24} height={24} color="black" />
              )}
            </button>
          )}

          <NotificationWidget />

          <Box w={40} h={40} visibleFrom="lg">
            <UserAvatar userName={fullName} avatarId={avatar} />
          </Box>

          <Burger
            data-testid="menu-button"
            opened={opened}
            onClick={toggle}
            size={28}
          />

          <Transition transition={scaleY} duration={500} mounted={opened}>
            {style => (
              <Portal target={document.body}>
                <NavigationMenu style={style} toggleMenuIsOpen={toggle} />
              </Portal>
            )}
          </Transition>
        </Flex>
      </Flex>
    </MBox>
  );
};

export default HeaderResponsive;
